import { ApplicationElement, html } from '@effectivastudio/lit-components';
import { csrfFetch } from '@effectivastudio/lit-components/lib/helpers.js';
import { live } from 'lit/directives/live.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import style from './style.lit.css';

import '@effectivastudio/lit-components/components/avatar/element.js';
import '@effectivastudio/lit-components/components/label/element.js';
import Snack from '@effectivastudio/snackbar/dist/snackbar.modern.js';

import translations from './translations.js';

import uniqBy from 'lodash-es/uniqBy.js';
import debounce from 'lodash-es/debounce.js';

/**
 * Date finder option component with options for voting on given dates or marking non-attendance
 *
 * @prop rid - Resource ID
 * @prop actionHref - Link for AJAX action for voting on server
 * @prop checked - Indicates (un)checked input state
 * @prop count - Vote count
 * @prop currentUser - Placeholder / Template function that needs to be defined where this component will be implemented
 * @prop disabled - If true, voting is disabled (optional, handled by wrapper component)
 * @prop href - Link to detailed results (optional)
 * @prop maxUsers - Maximum number of users that can be displayed (number, default: 5, optional)
 * @prop radioGroupName - When set, indicates single choice; also used for name for radio button groups
 * @prop resultsHidden - If present, avatars and results are hidden, message is displayed (optional, handled by wrapper component)
 * @prop showTime - If true, adds time to date (optional)
 * @prop skeleton - When true, renders placeholder for component while loading
 * @prop timestamp - Date offered, if not present, renders Cannot attend (optional)
 * @prop totalCount - Total vote count
 * @prop users - Users who voted (array)
 */

export default class DateFinderOption extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    rid: { type: String },
    actionHref: { type: String },
    checked: { type: Boolean },
    count: { type: Number },
    disabled: { type: Boolean },
    href: { type: String },
    maxUsers: { type: Number },
    radioGroupName: { type: String },
    resultsHidden: { type: Boolean },
    showTime: { type: Boolean },
    skeleton: { type: Boolean },
    timestamp: { type: Date },
    totalCount: { type: Number },
    users: { type: Array },
    initial: { type: Object, state: true },
  };

  constructor() {
    super();
    this.count = 0;
    this.totalCount = 0;
    this.maxUsers = 5;
    this.users = [];

    /** @internal */
    this.me = this.constructor.currentUser();
  }

  /** @internal */
  static translations = translations;

  /** @internal */
  get isCannotAttend() {
    return !this.timestamp;
  }

  /** @internal */
  get input() {
    return this.shadowRoot.querySelector('input');
  }

  /** @internal */
  get multiChoice() {
    return !this.radioGroupName;
  }

  /** @internal */
  get singleChoice() {
    return !!this.radioGroupName;
  }

  static currentUser = () => {
    console.warn('currentUser not implemented');
    return { name: 'Current user', picture: '' };
  };

  connectedCallback() {
    super.connectedCallback();

    /** @internal */
    this.initial = {
      count: this.count,
      checked: this.checked,
    };
  }

  renderUsers() {
    return html`
      <ef-avatar-list
        .users=${this.users}
        .totalCount=${this.count}
        url=${ifDefined(this.resultsHidden ? '' : this.href)}>
      </ef-avatar-list>
    `;
  }

  restoreState() {
    this.checked = !this.checked;
    this.count = this.initial.count;
  }

  async addUser() {
    this.checked = true;
    this.count += 1;
    this.users = uniqBy([this.me, ...this.users], 'name');
    this.uncheckOtherRadioButtons();

    await this.updateComplete;

    Snack({ text: this.t('snackbar.vote') });

    this.dispatchEvent(
      new Event('date-finder-option-add', { bubbles: true, composed: true })
    );
    this.dispatchEvent(
      new Event('date-finder-option-voted', { bubbles: true, composed: true })
    );
  }

  async removeUser() {
    this.checked = false;
    this.count -= 1;
    this.users = this.users.filter(user => user.name !== this.me.name);

    await this.updateComplete;

    Snack({ text: this.t('snackbar.un_vote') });

    this.dispatchEvent(
      new Event('date-finder-option-remove', { bubbles: true, composed: true })
    );
    this.dispatchEvent(
      new Event('date-finder-option-voted', { bubbles: true, composed: true })
    );
  }

  async onChange(e) {
    e.target.checked ? this.addUser() : this.removeUser();
    this.debouncedFetch();
  }

  // Simulate normal radio button behavior
  // because radios are in shadowRoot and don't see each other
  uncheckOtherRadioButtons() {
    const optionsInSameRadioGroup = document.querySelectorAll(
      `[radioGroupName="${this.radioGroupName}"]`
    );
    optionsInSameRadioGroup.forEach(r => {
      if (r !== this && r.checked) r.removeUser();
    });
  }

  /** @internal */
  fetch() {
    const beforeSendEvent = new Event('ajax:beforeSend', {
      bubbles: true,
      composed: true,
      cancelable: true,
    });

    this.dispatchEvent(beforeSendEvent);

    if (beforeSendEvent.defaultPrevented) return;

    csrfFetch(this.actionHref, {
      method: 'post',
    })
      .then(res => this.onFetchSuccess(res))
      .catch(res => this.onFetchError(res));
  }

  debouncedFetch = debounce(() => this.fetch(), 300);

  /** @internal */
  onFetchError() {
    this.dispatchEvent(
      new Event('ajax:error', { bubbles: true, composed: true })
    );
    this.dispatchEvent(
      new Event('ajax:complete', { bubbles: true, composed: true })
    );
    this.displaySnackBarError();
    setTimeout(() => {
      this.restoreState();
    }, 200);
  }

  /** @internal */
  onFetchSuccess() {
    this.dispatchEvent(
      new Event('ajax:success', { bubbles: true, composed: true })
    );
    this.dispatchEvent(
      new Event('ajax:complete', { bubbles: true, composed: true })
    );
  }

  /** @internal */
  displaySnackBarError() {
    Snack({
      text: this.t('snackbar.generic_error_msg'),
      customClass: 'snackbar-danger',
    });
  }

  renderResults() {
    return html`
      ${this.resultsHidden
        ? null
        : html`
            ${this.renderUsers()}

            <a class="info" href=${this.href}>
              <span>
                ${this.count} / ${this.t('votes', { count: this.totalCount })}
              </span>
            </a>
          `}
    `;
  }

  renderDate() {
    const formatted = this.i18n.strftime(this.timestamp, '%d,%b,%Y');
    const [day, month, year] = formatted.split(',');

    const time = this.i18n.l('date.formats.time', this.timestamp);

    return html`
      <div class="datetime-widget">
        ${this.showTime ? html`<div class="time-widget">${time}</div>` : null}

        <div class="date-widget">
          <div class="date-widget-day">${day}</div>
          <div class="date-widget-month">${month}</div>
          <div class="date-widget-year">${year}</div>
        </div>
      </div>
    `;
  }

  renderDateOrCannotAttend() {
    return html`
      ${this.timestamp
        ? this.renderDate()
        : html` <div class="attendance">
            <div>${this.t('cannot_attend')}</div>
          </div>`}
    `;
  }

  render() {
    return html`
      <div class="item">
        ${this.skeleton
          ? html`<span>${this.t('placeholder')}</span>`
          : html`${this.renderDateOrCannotAttend()}`}
        ${this.renderResults()}

        <div class="card-date-item-check">
          <input
            @input=${this.onChange}
            .checked=${live(this.checked)}
            id="x"
            class="custom-input"
            type="${this.multiChoice ? 'checkbox' : 'radio'}"
            name=${ifDefined(this.radioGroupName)}
            ?disabled=${this.disabled || this.skeleton} />
          <label for="x"></label>
        </div>
      </div>
    `;
  }
}

DateFinderOption.registerTagName('bu-date-finder-option');

customElements.get('bu-date-finder-option') ||
  customElements.define('bu-date-finder-option', DateFinderOption);
